/**
 * Cidades CRUD.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { MapPin, Plus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cidadesApi, type Cidade } from "@/lib/api-cadastros";

export function CidadesPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Partial<Cidade>>({ active: true });

  const list = useQuery({ queryKey: ["cidades"], queryFn: () => cidadesApi.list({ size: 200 }) });

  const create = useMutation({
    mutationFn: (body: Partial<Cidade>) => cidadesApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cidades"] });
      toast.success("Cidade criada");
      setOpen(false);
      setForm({ active: true });
    },
    onError: (e: Error) => toast.error("Falha", { description: e.message }),
  });

  const columns: ColumnDef<Cidade>[] = [
    {
      id: "name",
      accessorKey: "name",
      header: "Cidade",
      cell: ({ row }) => (
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-sky-50 text-sky-600 dark:bg-sky-500/10">
            <MapPin className="h-3.5 w-3.5" />
          </div>
          <div>
            <p className="font-medium">{row.original.name}</p>
            <p className="text-xs text-muted-foreground">{row.original.uf}</p>
          </div>
        </div>
      ),
    },
    {
      id: "ibge_code",
      accessorKey: "ibge_code",
      header: "Código IBGE",
      cell: ({ row }) => (
        <code className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-white/5 text-xs font-mono">
          {row.original.ibge_code ?? "—"}
        </code>
      ),
    },
    {
      id: "coords",
      header: "Coordenadas",
      cell: ({ row }) =>
        row.original.lat && row.original.lng ? (
          <span className="font-mono text-xs text-muted-foreground">
            {Number(row.original.lat).toFixed(4)}, {Number(row.original.lng).toFixed(4)}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      id: "active",
      accessorKey: "active",
      header: "Status",
      cell: ({ row }) =>
        row.original.active ? <Badge variant="success" dot>Ativa</Badge> : <Badge variant="muted" dot>Inativa</Badge>,
    },
  ];

  return (
    <>
      <PageHeader
        title="Cidades"
        description="Localidades atendidas. Usado pra endereços + geo do match engine."
        actions={
          <Button onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" /> Nova cidade
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={list.data?.items ?? []}
        loading={list.isLoading}
        searchPlaceholder="Buscar cidade, UF ou IBGE..."
        emptyTitle="Nenhuma cidade cadastrada"
        emptyDescription="Cadastre cidades atendidas pra geolocalização."
        emptyAction={<Button onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> Nova cidade</Button>}
        columnLabels={{
          name: "Cidade",
          ibge_code: "Código IBGE",
          coords: "Coordenadas",
          active: "Status",
        }}
      />

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Nova cidade</SheetTitle>
          </SheetHeader>
          <SheetBody className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Nome *</Label>
              <Input value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">UF *</Label>
                <Input
                  value={form.uf ?? ""}
                  onChange={(e) => setForm({ ...form, uf: e.target.value.toUpperCase().slice(0, 2) })}
                  maxLength={2}
                  className="font-mono uppercase"
                />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label className="text-xs">Código IBGE</Label>
                <Input
                  value={form.ibge_code ?? ""}
                  onChange={(e) => setForm({ ...form, ibge_code: e.target.value })}
                  className="font-mono"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Latitude</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={form.lat ?? ""}
                  onChange={(e) => setForm({ ...form, lat: parseFloat(e.target.value) })}
                  className="font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Longitude</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={form.lng ?? ""}
                  onChange={(e) => setForm({ ...form, lng: parseFloat(e.target.value) })}
                  className="font-mono"
                />
              </div>
            </div>
          </SheetBody>
          <SheetFooter>
            <SheetClose asChild><Button variant="outline">Cancelar</Button></SheetClose>
            <Button onClick={() => create.mutate(form)} disabled={!form.name || !form.uf || create.isPending}>
              {create.isPending ? "Criando..." : "Criar"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
